import React, { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    TextField,
    Button,
    Typography,
    Alert,
    Container,
    CssBaseline,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Link,
    Paper
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import logo from '../../images/logo.jpeg';

export default function ForgotPasswordPage() {
    const [email, setEmail] = useState('');
    const [role, setRole] = useState('doctor');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!email || !role) {
            setError('Please enter your email and select your role');
            return;
        }

        if (!validateEmail(email)) {
            setError('Please enter a valid email address');
            return;
        }

        setIsLoading(true);

        try {
            const API_URL = process.env.REACT_APP_API_URL;
            const response = await fetch(`${API_URL}/auth/forgot-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email,
                    role,
                }),
            });

            if (response.ok) {
                setSuccess('Password reset link has been sent to your email. Please check your inbox.');
                setEmail('');
                setRole('doctor');

                // Redirect to login after 3 seconds
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            } else {
                const errorData = await response.json();
                setError(errorData.detail || 'Failed to send password reset email. Please try again.');
            }
        } catch (err) {
            console.error('Forgot password error:', err);
            setError('An error occurred. Please try again later.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                backgroundImage: 'url(/assets/reset.png)',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                padding: { xs: 2, md: 8 },
            }}
        >
            <CssBaseline />
            <Paper
                elevation={6}
                sx={{
                    padding: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    width: '100%',
                    maxWidth: '450px',
                    borderRadius: 4,
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                }}
            >
                <Box
                    component="img"
                    src={logo}
                    alt="Cardireach Logo"
                    sx={{
                        width: 120,
                        height: 120,
                        objectFit: 'contain',
                        mb: 2
                    }}
                />

                <Typography component="h1" variant="h5" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Forgot Password
                </Typography>

                <Typography variant="body2" color="textSecondary" sx={{ mb: 3, textAlign: 'center' }}>
                    Enter your email address and we'll send you a link to reset your password
                </Typography>

                <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {success && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            {success}
                        </Alert>
                    )}

                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="email"
                        label="Email Address"
                        name="email"
                        autoComplete="email"
                        autoFocus
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={isLoading}
                        type="email"
                    />

                    <FormControl fullWidth margin="normal" required>
                        <InputLabel id="role-label">Your Role</InputLabel>
                        <Select
                            labelId="role-label"
                            id="role"
                            value={role}
                            label="Your Role"
                            onChange={(e) => setRole(e.target.value)}
                            disabled={isLoading}
                        >
                            <MenuItem value="doctor">Doctor</MenuItem>
                            <MenuItem value="marketinguser">Marketing User</MenuItem>
                        </Select>
                    </FormControl>

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{ mt: 3, mb: 2, py: 1.5 }}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <>
                                <CircularProgress size={20} sx={{ mr: 1 }} />
                                Sending...
                            </>
                        ) : (
                            'Send Reset Link'
                        )}
                    </Button>

                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                        <Link
                            component="button"
                            variant="body2"
                            onClick={() => navigate('/login')}
                            sx={{ cursor: 'pointer', fontWeight: 500 }}
                            type="button"
                            underline="hover"
                        >
                            Back to Login
                        </Link>
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
}
